// CommandCenterPage — UX v0.7 replacement for the long-scroll ProjectPage.
//
// Three panes:
//   * LEFT — Timeline rail (NOW / UP NEXT / DONE)
//   * CENTER — Focus surface (shows exactly one thing at a time)
//   * RIGHT — Inspector (Intent / Artifacts / Budget / Log tabs)
//
// The focus state is derived from actual backend state (briefing /
// gates / artifact index / RQS), not from a prototype-only switcher.
//
// Backend contracts remain identical — we reuse the existing
// PhaseBriefingPanel, PipelineRunner, GateReviewPanel, ArtifactViewer,
// DownloadCenter, and LiveDashboard components inside the Focus pane.
//
// Plan §P2.13 / §P2.14 / §P2.15 / §P2.19 / §P2.20 — UI Workflow
// Consistency Audit polish pass:
//
//   * The Focus surface gains a breadcrumb (Phase 3 of 5 — …).
//   * The Timeline rail's Done section is collapsed by default and
//     promotes Inputs into a first-class row alongside the phases.
//   * Page-scoped keyboard shortcuts: ``[`` / ``]`` cycle the focus,
//     ``1..5`` jump to the matching phase, ``i`` opens the Inputs tab.
//   * Loading states use shared Skeleton primitives instead of
//     ad-hoc spinners.

import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQueries, useQuery } from '@tanstack/react-query'
import {
  ChevronRight,
  Coins,
  File as FileIcon,
  FolderGit,
  Gauge,
  GitMerge,
  History,
  Layers,
  Map as MapIcon,
  ScrollText,
  Sparkles,
  Target,
  Upload,
} from 'lucide-react'

import { api, ApiError, projectFileUrl } from '@/lib/api'
import { getDocumentStructure } from '@/lib/documentStructureApi'
import { humanBytes, runFolder } from '@/lib/format'
import { extractGoals, firstParagraph, stripFrontmatter } from '@/lib/markdown'
import { useProjectEvents } from '@/hooks/useProjectEvents'
import { isEditableTarget } from '@/hooks/useGlobalShortcuts'
import { usePipelineStore } from '@/stores/pipelineStore'
import { useUxStore } from '@/stores/uxStore'
import { useCompliancePackStore } from '@/stores/compliancePackStore'
import { getShape } from '@/components/intake/shapes'
import { AUDIENCE_OPTIONS } from '@/components/intake/shapes'
import type { RunStateRecord } from '@/types'

import { PhaseBriefingPanel } from '@/components/briefings/PhaseBriefingPanel'
import { CarbonPhaseBriefingPanel } from '@/components/briefings/CarbonPhaseBriefingPanel'
import { PipelineRunner } from '@/components/pipeline/PipelineRunner'
import { ArtifactViewer } from '@/components/artifacts/ArtifactViewer'
import { DownloadCenter } from '@/components/downloads/DownloadCenter'
import { ContentAwareG1Gate } from '@/components/command-center/ContentAwareG1Gate'
import { ContentAwareG2Gate } from '@/components/command-center/ContentAwareG2Gate'
import { StubTriageList } from '@/components/compliance/StubTriageList'
import { ContentAwareG3Gate } from '@/components/command-center/ContentAwareG3Gate'
import { ContentAwareG35Gate } from '@/components/command-center/ContentAwareG35Gate'
// Gate G3.7 Phase 6 (plan §8) — SRD Technical Depth Audit panel.
import { ContentAwareG37Gate } from '@/components/command-center/ContentAwareG37Gate'
import { CoverageMapPanel } from '@/components/command-center/CoverageMapPanel'
import { ContentAwareG4Gate } from '@/components/command-center/ContentAwareG4Gate'

import { ContentAwareG5Gate } from '@/components/command-center/ContentAwareG5Gate'
import { ApprovalChainEditor } from '@/components/command-center/ApprovalChainEditor'
import { AiGovernanceRatificationCard } from '@/components/command-center/AiGovernanceRatificationCard'
import { InputUploader } from '@/components/intake/InputUploader'
import { SkeletonCard, SkeletonRow } from '@/components/ui/skeleton'
// Gate G3.7 Phase 0 primitives — sticky stepper, lock guard, skip
// affordance. Source: plan/AI_ALM_Gate_G3_7_Implementation_Plan.md §2.
import {
  PipelineStepper,
  type StepDescriptor,
} from '@/components/command-center/PipelineStepper'
import { SkipGate } from '@/components/command-center/SkipGate'
import { STEPPER_ORDER } from '@/components/command-center/focusKeys'
import type { ProjectContext } from '@/types'

// ---------------------------------------------------------------------- //
// Helpers
// ---------------------------------------------------------------------- //
type FocusKey =
  | 'briefing'
  | 'gate1'
  | 'pipeline2'
  | 'pipeline3'
  | 'pipeline4'
  | 'pipeline5'
  | 'gate2'
  | 'gate25'
  | 'gate3'
  // Quality-Uplift Q7 (plan §7.1 step 4) — half-step Coherence Gate
  // between Phase 3 and Phase 4. Surfaces non-truncation citation
  // findings + Q5 dedup needs_review rows.
  | 'gate35'
  // Gate G3.7 Phase 6 (plan ``AI_ALM_Gate_G3_7_Implementation_Plan.md``
  // §8) — SRD Technical Depth Audit. Sits after G3.5 and before
  // Phase 4. Surfaces SRD Depth Probe findings (per-shape rubric)
  // as Approve / Edit / Defer / Reject decisions.
  | 'gate37'
  | 'gate4'
  | 'gate5'
  | 'artifacts'
  | 'inputs'
  | 'export'
  | 'approvalChain'
  // 2026-05-28 — Pre-Export Review surface for AI-governance
  // ratification. Surfaces when the project's BR/SR text declares
  // an AI-derived output AND the architect didn't ratify at intake
  // (because the wizard's shape gating missed the case or the AI
  // signal only crystallised in Phase 3). Closes
  // PEG-AI-GOVERNANCE-SIGNATURE-001 post-hoc.
  | 'aiGovernance'

// One rung of the linear pipeline flow. ``n`` is the phase number for a
// phase rung, or the numeric gate id (1, 2, 25, 3, 35, 37, 4, 5) for a
// gate rung.
type LadderStep = { key: FocusKey; kind: 'phase' | 'gate'; n: number }


interface ArtifactIndexFile {
  path: string
  name: string
  size_bytes: number
  phase: number
}
interface ArtifactIndex {
  files: ArtifactIndexFile[]
}

// Friendly labels for the Phase-3 agent ids surfaced by the recipe
// registry. Mirrors ``AGENT_DISPLAY_NAMES`` in
// ``src/ai_alm/services/recipe_registry.py`` — when adding a new agent
// to the backend registry, add the matching label here so the
// Inspector can render it.
//
// Phase-H ripple (Agent Evaluation Implementation Plan §9.2): catches up
// the table with every author agent shipped through Phase G. The legacy
// ``safety`` slug stays for back-compat with persisted ``recipe_agents``
// lists predating the Phase-D split.
const AGENT_LABELS: Record<string, string> = {
  business_analyst: 'Business Analyst',
  system_architect: 'System Architect',
  // Phase-D split — ``safety`` legacy alias kept; new runs use the
  // split labels (``compliance`` + ``security_architect``) directly.
  safety: 'Safety / Compliance',
  compliance: 'Compliance',
  security_architect: 'Security Architect',
  diagrammer: 'Diagrammer',
  ui_sketcher: 'UI Sketcher',
  // Phase-C — API Contract Agent.
  api_contract_agent: 'API Contract Agent',
  // Phase-F — Test Engineer.
  test_engineer: 'Test Engineer',
  // Phase-G — UX Researcher.
  ux_researcher: 'UX Researcher',
}

// ---------------------------------------------------------------------- //
// Focus metadata — drives the breadcrumb (P2.13) and ``1..5`` keyboard
// shortcuts. Pre-computed once because the trail copy is invariant.
// ---------------------------------------------------------------------- //
interface FocusMeta {
  /** Stage (Briefing / Phase / Gate / Export). */
  stage: string
  /** Position label: e.g. "Phase 3 of 5" or "Gate G2". */
  position: string
  /** Friendly title for the surface. */
  title: string
}

const FOCUS_META: Record<FocusKey, FocusMeta> = {
  briefing: { stage: 'Briefing', position: 'Phase 1 briefing', title: 'Pre-flight check' },
  gate1: { stage: 'Gate', position: 'Gate G1', title: 'Phase 1 review' },
  pipeline2: { stage: 'Phase', position: 'Phase 2 of 5', title: 'Observer · constraint extraction' },
  gate2: { stage: 'Gate', position: 'Gate G2', title: 'Constraint review' },
  gate25: { stage: 'Gate', position: 'Gate G2.5', title: 'Compliance Triage' },
  pipeline3: { stage: 'Phase', position: 'Phase 3 of 5', title: 'Generation Swarm' },
  gate3: { stage: 'Gate', position: 'Gate G3', title: 'Swarm sanity check' },
  // Q7 G3.5 — Coherence Gate sits between G3 and Phase 4; the
  // breadcrumb position uses the half-step label so the architect
  // sees "Gate G3.5" inline with the rest of the ladder.
  gate35: { stage: 'Gate', position: 'Gate G3.5', title: 'Coherence Gate' },
  // Gate G3.7 (plan/AI_ALM_Gate_G3_7_Implementation_Plan.md §8) —
  // Technical Depth Audit lives between G3.5 and Phase 4. Same
  // half-step labelling treatment as G3.5.
  gate37: { stage: 'Gate', position: 'Gate G3.7', title: 'Technical Depth Audit' },
  pipeline4: { stage: 'Phase', position: 'Phase 4 of 5', title: 'Critic audit' },

  gate4: { stage: 'Gate', position: 'Gate G4', title: 'Rewrite review' },
  pipeline5: { stage: 'Phase', position: 'Phase 5 of 5', title: 'Exporter' },
  gate5: { stage: 'Gate', position: 'Gate G5', title: 'Final review' },
  artifacts: { stage: 'Files', position: 'Artifact Viewer', title: 'Artifacts' },
  inputs: { stage: 'Inputs', position: 'Project inputs', title: 'Uploaded materials' },
  export: { stage: 'Export', position: 'Download Center', title: 'Final package' },
  approvalChain: { stage: 'Intake', position: 'Approval Chain', title: 'Required signatories' },
  aiGovernance: { stage: 'Pre-export', position: 'AI Governance', title: 'Ratify AI-derived outputs' },
}

// ---------------------------------------------------------------------- //
// Main page
// ---------------------------------------------------------------------- //
export function CommandCenterPage({
  legacyBriefing = false,
}: {
  legacyBriefing?: boolean
} = {}) {
  const navigate = useNavigate()
  const { projectId, runId: runIdParam } = useParams<{
    projectId: string
    runId?: string
  }>()

  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId!),
    enabled: Boolean(projectId),
  })

  const runId =
    Number(runIdParam) ||
    (project.data?.current_run && project.data.current_run > 0 ? project.data.current_run : 1)

  const context = useQuery({
    queryKey: ['context', projectId],
    queryFn: () => api.getIntake(projectId!),
    enabled: Boolean(projectId),
    retry: false,
  })

  // Feed live WS events into the pipeline store.
  useProjectEvents(projectId ?? null, runId)

  // ------------------------------------------------------------------ //
  // Resume after reboot — load the persisted ``run-state.json`` and feed
  // it into the in-memory pipelineStore. This is what makes a refresh
  // (or a server reboot) preserve the token meter, the per-agent stats,
  // the RQS breakdown, and the user's last surface, instead of snapping
  // every dashboard back to zero.
  //
  // The endpoint always returns 200 (with an empty record on first
  // visit) so we don't need a 404 path. We refetch every 30 s as a
  // safety net in case the WebSocket missed an event — in practice
  // the live socket is the primary path and this is just for resume.
  // ------------------------------------------------------------------ //
  const runState = useQuery<RunStateRecord>({
    queryKey: ['run-state', projectId, runId],
    queryFn: () => api.getRunState(projectId!, runId),
    enabled: Boolean(projectId),
    retry: false,
    refetchInterval: 30_000,
  })
  const hydrateFromRunState = usePipelineStore((s) => s.hydrateFromRunState)
  useEffect(() => {
    if (runState.data) {
      hydrateFromRunState(runState.data)
    }
  }, [runState.data, hydrateFromRunState])

  // Artifact index drives phase completeness + timeline states.
  const index = useQuery<ArtifactIndex>({
    queryKey: ['artifact-index', projectId, runId],
    queryFn: async () => {
      const res = await fetch(
        `/api/projects/${encodeURIComponent(projectId!)}/runs/${runId}/artifacts`,
      )
      if (!res.ok) throw new Error(`index fetch failed: ${res.status}`)
      return (await res.json()) as ArtifactIndex
    },
    enabled: Boolean(projectId),
    retry: false,
    refetchInterval: 5000,
  })

  // Briefing + gates — used by the rail to know who "needs you".
  const briefing = useQuery({
    queryKey: ['briefing', projectId, runId, 1],
    queryFn: () => api.getBriefing(projectId!, runId, 1),
    enabled: Boolean(projectId),
    retry: false,
  })

  // Derived phase states.
  const filenames = useMemo(() => {
    const s = new Set<string>()
    for (const f of index.data?.files ?? []) s.add(f.name)
    return s
  }, [index.data])

  // Gate1 only exists once Phase 1 has produced the charter; until then
  // the GET would 404 on every refetch and spam the console. The
  // artifact-index poll already tells us when that file lands.
  const gate1 = useQuery({
    queryKey: ['gate', projectId, runId, 1],
    queryFn: () => api.getGate(projectId!, runId, 1),
    enabled: Boolean(projectId) && filenames.has('project-charter.md'),
    retry: false,
  })

  // Stepper truthiness for the middle/late gates. We fetch each one
  // only when its ``G{N}-gate.md`` is present in the artifact index so
  // we don't spam 404s before the panel has emitted. Cache keys mirror
  // the per-panel queries (``['gate', projectId, runId, N]``) so the
  // panel and stepper share a single source of truth — no duplicate
  // network calls.
  // G3 (Swarm sanity, thorough-only) IS fetched now (2026-07-02): it
  // carries 4 real spot-check decisions and the strict flow-lock blocks
  // Phase 4 while it's pending. Without a real status the rail rendered
  // G3 as a permanently-locked padlock the architect couldn't open —
  // so P4 was blocked by a gate with no answerable surface. The query
  // is enabled only when ``G3-gate.md`` is on disk, so at standard /
  // minimal budgets (where G3 is never emitted) it stays a no-op and
  // the row keeps its unemitted→skipped behaviour.
  const STEPPER_GATE_NUMBERS = [2, 3, 25, 35, 37, 4, 5] as const
  const STEPPER_GATE_FILES: Record<(typeof STEPPER_GATE_NUMBERS)[number], string> = {
    2: 'G2-gate.md',
    3: 'G3-gate.md',
    25: 'G2.5-gate.md',
    35: 'G3.5-gate.md',
    37: 'G3.7-gate.md',
    4: 'G4-gate.md',
    5: 'G5-gate.md',
  }
  const stepperGateQueries = useQueries({
    queries: STEPPER_GATE_NUMBERS.map((n) => ({
      queryKey: ['gate', projectId, runId, n],
      queryFn: () => api.getGate(projectId!, runId, n),
      enabled: Boolean(projectId) && filenames.has(STEPPER_GATE_FILES[n]),
      retry: false,
      // Refresh on the same cadence as the panel's own poll so the
      // pill transitions to ``done`` shortly after the architect
      // approves a gate, without forcing a page reload.
      refetchInterval: 5000 as number,
    })),
  })
  const gateStatusByNumber = useMemo(() => {
    const out: Partial<Record<number, string | null>> = {}
    STEPPER_GATE_NUMBERS.forEach((n, i) => {
      out[n] = stepperGateQueries[i].data?.status ?? null
    })
    return out
  // The query *array* identity is stable across renders; we depend on
  // the underlying data so the memo re-runs when any gate's status
  // actually changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, stepperGateQueries.map((q) => q.data?.status))

  const phaseStates = useMemo(() => {
    const pending: Record<1 | 2 | 3 | 4 | 5, 'done' | 'blocked' | 'ready'> = {
      1: filenames.has('project-charter.md') ? 'done' : 'ready',
      2: filenames.has('constraint-log.json') ? 'done' : filenames.has('project-charter.md') ? 'ready' : 'blocked',
      3: filenames.has('brd.md') && filenames.has('srd.md') ? 'done' : filenames.has('constraint-log.json') ? 'ready' : 'blocked',
      4: filenames.has('audit-diff.md') ? 'done' : filenames.has('brd.md') ? 'ready' : 'blocked',
      5: filenames.has('requirements.md') || filenames.has('export.zip')
        ? 'done'
        : filenames.has('audit-diff.md')
        ? 'ready'
        : 'blocked',
    }
    return pending
  }, [filenames])

  // Decide initial focus based on what actually needs attention.
  //
  // Rule: keep the user on the 'briefing' surface until Phase 1 has
  // actually produced artifacts, because the "Run Phase 1" button lives
  // inside the briefing panel itself.
  //
  // ``last_focus`` from the persisted run state takes priority when
  // present — that's the surface the user was on when they last left
  // the project, and the explicit user request is "send me back to
  // where I was". Only when the resume hint is missing or invalid do
  // we fall back to the heuristic ladder below.
  const VALID_FOCUS_KEYS: ReadonlySet<FocusKey> = new Set([
    'briefing',
    'gate1',
    'gate2',
    'gate25',
    'gate3',
    'gate35',
    'gate37',
    'gate4',
    'gate5',
    'pipeline2',
    'pipeline3',
    'pipeline4',
    'pipeline5',
    'artifacts',
    'inputs',
    'export',
  ])

  const [focus, setFocusInternal] = useState<FocusKey | null>(null)
  // Wrap setFocus so every user-driven focus change is mirrored to disk.
  // This is what makes "go back to where I was" survive a reboot — the
  // backend records the surface key and the next mount reads it back.
  const setFocus = (next: FocusKey) => {
    setFocusInternal(next)
    if (projectId) {
      // Best-effort persistence — never block UI on the round-trip.
      void api
        .setRunFocus(projectId, runId, next)
        .catch(() => {
          /* ignore — focus is a UX hint, not a correctness invariant */
        })
    }
  }
  useEffect(() => {
    if (focus !== null) return
    // Resume hint takes priority. Wait for the run-state query to settle
    // (success OR error) so we don't race the heuristic into a wrong
    // surface only to have it stomped on the next render.
    if (runState.isLoading) return
    const hinted = runState.data?.last_focus
    if (hinted && VALID_FOCUS_KEYS.has(hinted as FocusKey)) {
      setFocusInternal(hinted as FocusKey)
      return
    }
    // No briefing emitted yet → stay on briefing so the user can re-emit.
    if (!briefing.data) {
      setFocusInternal('briefing')
      return
    }
    // Briefing not approved, or approved but P1 hasn't run → briefing focus.
    if (briefing.data.status !== 'approved' || phaseStates[1] !== 'done') {
      setFocusInternal('briefing')
      return
    }
    // P1 done but G1 still open → send user to G1.
    if (gate1.data?.status !== 'approved') {
      setFocusInternal('gate1')
      return
    }
    // Downstream focus rules.
    if (phaseStates[5] === 'done') { setFocusInternal('export'); return }
    if (phaseStates[4] === 'done') { setFocusInternal('pipeline5'); return }
    if (phaseStates[3] === 'done') { setFocusInternal('pipeline4'); return }
    if (phaseStates[2] === 'done') { setFocusInternal('pipeline3'); return }
    setFocusInternal('pipeline2')
  }, [
    briefing.data,
    gate1.data,
    phaseStates,
    focus,
    runState.isLoading,
    runState.data?.last_focus,
  ])

  // -------------------------------------------------------------------- //
  // Page-level keyboard shortcuts — plan §P2.19 / audit §3.2.4. Lives
  // inside the page (not the global hook) because it needs ``setFocus``.
  // ``[`` / ``]`` cycle the focus through the visible workflow ladder;
  // ``1`` … ``5`` jump to the matching phase; ``i`` opens the Inputs
  // tab. We explicitly skip when the user is typing into a field.
  // -------------------------------------------------------------------- //
  //
  // Cadence resolution mirrors the backend's gate enforcer (see
  // ``src/ai_alm/services/gate_enforcer.py`` lines 167–174):
  //
  //     budget = derived.forced_intervention_budget or intake.intervention_budget
  //
  // The backend escalates the budget to ``thorough`` for high-risk
  // domains (FedRAMP / NIST / safety-critical), persisted as
  // ``derived.forced_intervention_budget``. The exporter's strict gate
  // enforcer (Q1) blocks Phase 5 against the *forced* budget, so the
  // rail must use the same value — otherwise the user sees a "Phase 5
  // blocked — N decision(s) still pending across G3" error referencing
  // a gate the UI has hidden. Without this fallback the architect has
  // no surface to answer the pending decisions on.
  const cadence =
    context.data?.derived.forced_intervention_budget ??
    context.data?.intake.intervention_budget ??
    'standard'
  const visibleGates = useMemo(() => {
    if (cadence === 'minimal') return [1, 4, 5]
    if (cadence === 'thorough') return [1, 2, 3, 4, 5]
    return [1, 2, 4, 5]
  }, [cadence])
  const hasComplianceCues = Boolean(
    context.data?.intake?.regulatory &&
      context.data.intake.regulatory.length > 0 &&
      !context.data.intake.regulatory.includes('none'),
  )

  // ------------------------------------------------------------------ //
  // The linear flow — ONE source of truth (owner-simplified 2026-07-02).
  // Two checks, nothing more:
  //   1. A step is COMPLETE when a phase has produced its artifacts, or
  //      a gate has every decision resolved (emitted + non-pending).
  //   2. The wall sits at the FIRST incomplete step: that step is the
  //      only actionable one ("Now"); everything after it is locked in
  //      navigation and no later phase can be run. Reaching a step
  //      already proves the steps before it were completed in order —
  //      so there are no per-phase prior-gate lists anywhere.
  // The rail, the "Now" cursor, the breadcrumb, and the Pipeline run
  // buttons all read THIS. No surface re-derives gate state.
  // ------------------------------------------------------------------ //
  const flowLadder = useMemo<LadderStep[]>(() => {
    const steps: LadderStep[] = [{ key: 'briefing', kind: 'phase', n: 1 }]
    steps.push({ key: 'gate1', kind: 'gate', n: 1 })
    steps.push({ key: 'pipeline2', kind: 'phase', n: 2 })
    if (visibleGates.includes(2)) steps.push({ key: 'gate2', kind: 'gate', n: 2 })
    if (hasComplianceCues) steps.push({ key: 'gate25', kind: 'gate', n: 25 })
    steps.push({ key: 'pipeline3', kind: 'phase', n: 3 })
    if (visibleGates.includes(3)) steps.push({ key: 'gate3', kind: 'gate', n: 3 })
    if (cadence !== 'minimal') steps.push({ key: 'gate35', kind: 'gate', n: 35 })
    if (cadence !== 'minimal') steps.push({ key: 'gate37', kind: 'gate', n: 37 })
    steps.push({ key: 'pipeline4', kind: 'phase', n: 4 })
    if (visibleGates.includes(4)) steps.push({ key: 'gate4', kind: 'gate', n: 4 })
    steps.push({ key: 'pipeline5', kind: 'phase', n: 5 })
    if (visibleGates.includes(5)) steps.push({ key: 'gate5', kind: 'gate', n: 5 })
    return steps
  }, [visibleGates, hasComplianceCues, cadence])

  const gateResolvedByNumber = (n: number): boolean => {
    const s = n === 1 ? (gate1.data?.status ?? null) : gateStatusByNumber[n]
    return Boolean(s) && s !== 'pending'
  }
  const isStepComplete = (s: LadderStep): boolean =>
    s.kind === 'phase'
      ? phaseStates[s.n as 1 | 2 | 3 | 4 | 5] === 'done'
      : gateResolvedByNumber(s.n)
  const firstIncompleteIdx = flowLadder.findIndex((s) => !isStepComplete(s))
  // Navigation check: a step is locked while any earlier step is still
  // incomplete.
  const isStepLocked = (key: FocusKey): boolean => {
    const i = flowLadder.findIndex((s) => s.key === key)
    return i !== -1 && firstIncompleteIdx !== -1 && i > firstIncompleteIdx
  }
  // "Now" = the first incomplete step.
  const currentStepKey: FocusKey | null =
    firstIncompleteIdx === -1 ? null : flowLadder[firstIncompleteIdx].key
  // The one phase (2–5) whose turn it is — the only phase whose Run
  // button is enabled. Null when the current step is a gate (answer it
  // first) or the run is complete.
  const runnablePhase: 2 | 3 | 4 | 5 | null = (() => {
    if (firstIncompleteIdx === -1) return null
    const cur = flowLadder[firstIncompleteIdx]
    return cur.kind === 'phase' && cur.n >= 2 && cur.n <= 5
      ? (cur.n as 2 | 3 | 4 | 5)
      : null
  })()

  // Keyboard [ ] cycling walks the same ladder (+ Export at the end).
  const focusLadder = useMemo<FocusKey[]>(
    () => [...flowLadder.map((s) => s.key), 'export'],
    [flowLadder],
  )

  useEffect(() => {
    if (!focus) return
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (isEditableTarget(e.target)) return
      const key = e.key
      // Cycle with ``[`` / ``]``.
      if (key === '[' || key === ']') {
        const idx = focusLadder.indexOf(focus)
        if (idx === -1) return
        const delta = key === ']' ? 1 : -1
        const next = focusLadder[(idx + delta + focusLadder.length) % focusLadder.length]
        if (next) {
          e.preventDefault()
          setFocus(next)
        }
        return
      }
      // Jump to a phase by number.
      if (/^[1-5]$/.test(key)) {
        const target =
          key === '1'
            ? 'briefing'
            : (`pipeline${key}` as FocusKey)
        e.preventDefault()
        setFocus(target)
        return
      }
      // ``i`` jumps to the Inputs surface.
      if (key === 'i' || key === 'I') {
        e.preventDefault()
        setFocus('inputs')
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
    // setFocus is stable; including it would re-bind on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, focusLadder])

  // -------------------------------------------------------------------- //

  if (!projectId) {
    return (
      <div className="text-slate-100 p-8">
        <p className="text-slate-400">Missing project id.</p>
      </div>
    )
  }

  if (project.isLoading) {
    // Audit §8.5 / plan §P2.20 — replace the centred Loader2 with a
    // skeleton card so the loading state matches the eventual layout.
    return (
      <div className="text-slate-100 p-8 max-w-3xl mx-auto">
        <SkeletonCard lines={6} />
      </div>
    )
  }

  if (project.error || !project.data) {
    return (
      <div className="text-slate-100 p-8">
        <p className="text-red-300 mb-2">Project not found.</p>
        <button
          type="button"
          onClick={() => navigate('/projects')}
          className="text-indigo-300 hover:underline text-sm"
        >
          ← back to projects
        </button>
      </div>
    )
  }

  const intakeMissing =
    context.error instanceof ApiError && context.error.status === 404

  // E2E hook (T2 — plan §5.5 / check-testids.mjs). Marks the
  // Command Center as fully hydrated. Only the *main* return path
  // ships this attribute — the early loading / error / not-found
  // paths intentionally do not, so a Playwright spec waiting on
  // ``[data-testid=command-center-ready]`` will only resolve once
  // the page is actually interactive.
  const ready =
    Boolean(project.data) &&
    !runState.isLoading &&
    !context.isLoading &&
    !index.isLoading &&
    !briefing.isLoading

  return (
    <div
      data-testid={ready ? 'command-center-ready' : undefined}
      className="text-slate-100 min-h-[calc(100vh-96px)] bg-slate-950"
    >
      {/* Secondary project header strip — lives below the main app bar */}
      <ProjectHeader
        projectId={projectId}
        runId={runId}
        projectName={project.data.name}
        problemStatement={project.data.problem_statement ?? ''}
        status={project.data.status}
        rewinds={project.data.rewind_count}
        phaseStates={phaseStates}
        intakeMissing={intakeMissing}
      />

      {intakeMissing ? (
        <div className="max-w-3xl mx-auto p-6">
          <div className="rounded-xl border border-amber-700/50 bg-amber-950/30 p-6 text-sm">
            <p className="font-semibold text-amber-200">Intake not submitted yet</p>
            <p className="text-slate-300 mt-1">
              This project hasn't completed the Smart Intake flow. Start it now to seed the
              Phase-1 briefing.
            </p>
            <button
              type="button"
              onClick={() => navigate('/intake')}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-indigo-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-400"
            >
              <Sparkles className="h-4 w-4" /> Start intake
            </button>
          </div>
        </div>
      ) : (
        <>
        {/* Gate G3.7 Phase 0 §2.2.1 — sticky pipeline stepper. Lives
            above the three-pane grid so it spans the full viewport
            and the architect always sees their position in the
            ladder. The descriptor list is computed from the same
            phaseStates / gate visibility we use for the rail. */}
        <PipelineStepper
          steps={buildStepperDescriptors({
            phaseStates,
            briefingApproved: briefing.data?.status === 'approved',
            // Resolved = any non-pending status (approved / modified /
            // rejected) — mirrors ``isGateResolved`` for G2+ so a G1
            // cleared via edits (rollup ``modified``) shows ✓ instead of
            // a stale amber "attention" pill.
            gate1Approved: Boolean(gate1.data?.status) && gate1.data?.status !== 'pending',
            gateStatusByNumber,
            cadence,
            hasComplianceCues: Boolean(
              context.data?.intake?.regulatory &&
                context.data.intake.regulatory.length > 0 &&
                !context.data.intake.regulatory.includes('none'),
            ),
            current: focus,
          })}
          current={focus}
          onNavigate={(f) => setFocus(f as FocusKey)}
        />
        <div
          className="grid gap-px bg-slate-800/50"
          style={{ gridTemplateColumns: '260px 1fr 360px', minHeight: 'calc(100vh - 220px)' }}
        >

          <TimelineRail
            projectName={project.data.name}
            projectId={projectId}
            runId={runId}
            briefingStatus={briefing.data?.status ?? 'pending'}
            gate1Status={gate1.data?.status ?? 'pending'}
            phaseStates={phaseStates}
            focus={focus}
            setFocus={setFocus}
            cadence={cadence}
            hasComplianceCues={hasComplianceCues}
            isLocked={isStepLocked}
            firstUnresolved={currentStepKey}
          />
          <FocusSurface
            projectId={projectId}
            runId={runId}
            project={project.data}
            focus={focus ?? 'briefing'}
            setFocus={setFocus}
            phaseStates={phaseStates}
            cadence={cadence}
            hasComplianceCues={hasComplianceCues}
            runnablePhase={runnablePhase}
            legacyBriefing={legacyBriefing}
          />

          <InspectorRail
            projectId={projectId}
            runId={runId}
            context={context.data ?? null}
            index={index.data ?? null}
            indexLoading={index.isLoading}
          />
        </div>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------- //
// Stepper descriptor builder — Gate G3.7 Phase 0 §2.2.1.
//
// Translates the page's existing phase / gate / briefing state into the
// PipelineStepper's StepDescriptor list. Pure function so the stepper
// only re-renders when the underlying state actually changes.
//
// Visibility filter mirrors the rail: gates hidden by intervention
// budget collapse into a "skipped" pill (the architect intentionally
// elided the gate by choosing a lighter cadence). Gates that are
// pending but whose precondition isn't met yet show as "locked".
// ---------------------------------------------------------------------- //
function buildStepperDescriptors(args: {
  phaseStates: Record<1 | 2 | 3 | 4 | 5, 'done' | 'blocked' | 'ready'>
  briefingApproved: boolean
  gate1Approved: boolean
  /**
   * Latest gate status per numeric gate id (2, 25, 35, 37, 4, 5). The
   * stepper uses this to mark a pill ``done`` as soon as the gate
   * reaches a terminal status — independent of whether the downstream
   * phase has run. Without this, an approved G3.5 / G3.7 would stay
   * ``locked`` until Phase 4 lands.
   */
  gateStatusByNumber: Partial<Record<number, string | null>>
  cadence: 'minimal' | 'standard' | 'thorough'
  hasComplianceCues: boolean
  current: FocusKey | null
}): StepDescriptor[] {
  const {
    phaseStates,
    briefingApproved,
    gate1Approved,
    gateStatusByNumber,
    cadence,
    hasComplianceCues,
    current,
  } = args

  // Any non-pending gate status is "the architect made a decision" —
  // approved, modified (some edits), rejected (some false-positives),
  // skipped (architect-recorded reason). The breadcrumb treats all of
  // those as ``done``; only ``pending`` keeps the pill locked. This
  // matches the gate-enforcer's contract (it blocks downstream phases
  // only on ``pending`` decisions, not on aggregate gate rollup).
  const isGateResolved = (n: number): boolean => {
    const s = gateStatusByNumber[n]
    return Boolean(s) && s !== 'pending'
  }
  // Gate has been emitted on disk but the architect still owes a
  // decision. Mirrors the ``pending`` rollup that
  // ``gate_enforcer.assert_gates_clear`` keys off — these are the
  // gates that will block downstream phases until answered. We pull
  // them out of ``locked`` so the breadcrumb stops mis-signalling
  // "blocked by something earlier" when really the architect is the
  // blocker.
  const isGatePending = (n: number): boolean => {
    return gateStatusByNumber[n] === 'pending'
  }

  // Visibility per the rail: minimal hides G2/G3/G3.5/G3.7; standard
  // hides G3 only; thorough shows everything. G2.5 only fires when
  // the intake declared compliance cues. G3.7 (depth audit) mirrors
  // G3.5 — auto-skipped at minimal, rendered at standard + thorough.
  const hiddenGates = new Set<FocusKey>()
  if (cadence === 'minimal') {
    hiddenGates.add('gate2')
    hiddenGates.add('gate3')
    hiddenGates.add('gate35')
    hiddenGates.add('gate37')
  } else if (cadence === 'standard') {
    hiddenGates.add('gate3')
  }
  if (!hasComplianceCues) hiddenGates.add('gate25')


  // Done / current map. ``done`` is true when the focus key has
  // observably completed; ``current`` is the architect's active
  // focus surface.
  function stateFor(key: FocusKey): StepDescriptor['state'] {
    if (current === key) return 'current'
    if (hiddenGates.has(key)) return 'skipped'
    switch (key) {
      case 'briefing':
        // P1/G1 UX fix — the briefing is never "locked": it's the entry
        // surface and always actionable until Phase 1 has run. Show
        // ``attention`` (amber, clickable) instead of a grey lock so the
        // stepper points at the real next action.
        return briefingApproved && phaseStates[1] === 'done' ? 'done' : 'attention'
      case 'gate1':
        // Once Phase 1 lands, the gate owes the architect a decision —
        // that's ``attention`` (same treatment as G2+ pending gates),
        // not ``locked``.
        if (gate1Approved) return 'done'
        return phaseStates[1] === 'done' ? 'attention' : 'locked'
      case 'pipeline2':
        return phaseStates[2] === 'done'
          ? 'done'
          : phaseStates[2] === 'ready'
          ? 'locked' // ready means unlocked but not done — still navigable
          : 'locked'
      case 'gate2':
        if (isGateResolved(2)) return 'done'
        if (isGatePending(2)) return 'attention'
        return 'locked'
      case 'gate25':
        if (isGateResolved(25)) return 'done'
        if (isGatePending(25)) return 'attention'
        return 'locked'
      case 'pipeline3':
        return phaseStates[3] === 'done' ? 'done' : 'locked'
      case 'gate3':
        if (isGateResolved(3)) return 'done'
        if (isGatePending(3)) return 'attention'
        return 'locked'
      case 'gate35':
        if (isGateResolved(35)) return 'done'
        if (isGatePending(35)) return 'attention'
        return 'locked'
      case 'gate37':
        // Mirrors gate35 — same upstream phase (Phase 3 produces the
        // probe envelope). ``done`` as soon as the gate reaches a
        // terminal status (approved / modified / rejected / skipped);
        // does NOT wait on Phase 4 because rejecting findings as false
        // positives rolls the gate up to ``rejected`` while still
        // counting as resolved per the enforcer contract.
        if (isGateResolved(37)) return 'done'
        if (isGatePending(37)) return 'attention'
        return 'locked'
      case 'pipeline4':

        return phaseStates[4] === 'done' ? 'done' : 'locked'
      case 'gate4':
        if (isGateResolved(4)) return 'done'
        if (isGatePending(4)) return 'attention'
        return 'locked'
      case 'pipeline5':
        return phaseStates[5] === 'done' ? 'done' : 'locked'
      case 'gate5':
        if (isGateResolved(5)) return 'done'
        if (isGatePending(5)) return 'attention'
        return 'locked'
      case 'export':
        return phaseStates[5] === 'done' ? 'done' : 'locked'
      default:
        return 'locked'
    }
  }

  return STEPPER_ORDER.map((key) => ({
    key,
    state: stateFor(key as FocusKey),
  }))
}


// ---------------------------------------------------------------------- //
// ProjectHeader
// ---------------------------------------------------------------------- //
function ProjectHeader({
  projectId,
  runId,
  projectName,
  problemStatement,
  status,
  rewinds,
  phaseStates,
  intakeMissing,
}: {
  projectId: string
  runId: number
  projectName: string
  problemStatement: string
  status: string
  rewinds: number
  phaseStates: Record<1 | 2 | 3 | 4 | 5, 'done' | 'blocked' | 'ready'>
  intakeMissing: boolean
}) {
  const uxProject = useUxStore((s) => s.projects[projectId])
  const shape = uxProject?.shape ? getShape(uxProject.shape) : null
  const tokensUsed = usePipelineStore((s) => s.tokensUsed)
  const tokensBudget = usePipelineStore((s) => s.tokensBudget)
  const pct = tokensBudget > 0 ? Math.min(100, (tokensUsed / tokensBudget) * 100) : 0

  return (
    <div className="border-b border-slate-800 bg-slate-900/60">
      <div className="px-6 py-3 flex flex-wrap items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg font-bold tracking-tight truncate">{projectName}</h1>
            <span className="alm-chip">{status.replace('_', ' ')}</span>
            <span className="alm-chip">run #{runId}</span>
            {rewinds > 0 && (
              <span className="alm-chip alm-chip-warn">
                <History className="h-3 w-3" /> {rewinds} rewind{rewinds === 1 ? '' : 's'}
              </span>
            )}
            {shape && (
              <span className="alm-chip alm-chip-info">
                <Layers className="h-3 w-3" /> {shape.label}
              </span>
            )}
          </div>
          {problemStatement && (
            <p className="text-xs text-slate-500 mt-1 line-clamp-1 font-mono">
              {projectId}
            </p>
          )}
        </div>

        {!intakeMissing && (
          <div className="flex items-center gap-3">
            {/* Phase dots */}
            <div className="flex items-center gap-1.5 text-[11px] font-semibold">
              {([1, 2, 3, 4, 5] as const).map((p) => {
                const state = phaseStates[p]
                const cls =
                  state === 'done'
                    ? 'alm-chip-ok'
                    : state === 'ready'
                    ? 'alm-chip-info'
                    : ''
                return (
                  <span key={p} className={`alm-chip ${cls}`}>
                    P{p}
                    {state === 'done' ? ' ✓' : state === 'ready' ? ' ●' : ' ○'}
                  </span>
                )
              })}
            </div>

            {/* Tokens meter */}
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Coins className="h-3.5 w-3.5" />
              {/* Audit §3.3.4 — when nothing has been spent yet, show
                  "Not started" so a brand-new project doesn't surface
                  a misleading 0% meter as if a budget had been seen. */}
              {tokensBudget > 0 && tokensUsed > 0 ? (
                <>
                  <div className="w-28 alm-progress-track">
                    <div className="alm-progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="font-mono tabular-nums">{Math.round(pct)}%</span>
                </>
              ) : (
                <span className="font-mono tabular-nums text-slate-500">
                  not started
                </span>
              )}
            </div>

            <Link
              to={`/projects/${encodeURIComponent(projectId)}/audit?runId=${runId}`}
              data-testid="nav-audit"
              title="Open the audit log for this run"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-700/60 bg-slate-900/40 px-2 py-1 text-xs text-slate-300 hover:border-slate-500 hover:text-white"
            >
              <ScrollText className="h-3.5 w-3.5" />
              Audit
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------- //
// TimelineRail (LEFT)
// ---------------------------------------------------------------------- //
function TimelineRail({
  projectId,
  runId,
  projectName,
  briefingStatus,
  phaseStates,
  focus,
  setFocus,
  cadence,
  hasComplianceCues,
  isLocked,
  firstUnresolved,
}: {
  projectId: string
  runId: number
  projectName: string
  briefingStatus: string
  gate1Status: string
  phaseStates: Record<1 | 2 | 3 | 4 | 5, 'done' | 'blocked' | 'ready'>
  focus: FocusKey | null
  setFocus: (f: FocusKey) => void
  cadence: 'minimal' | 'standard' | 'thorough'
  hasComplianceCues: boolean
  // Navigation + "Now" both come from the single flow ladder computed in
  // the parent (see the flow-ladder block in CommandCenterPage) — the
  // rail no longer re-derives gate state or ordering.
  isLocked: (key: FocusKey) => boolean
  firstUnresolved: FocusKey | null
}) {
  // Which gates to show in the rail based on the cadence (plan §M3.2).
  const visibleGates = useMemo(() => {
    if (cadence === 'minimal') return [1, 4, 5]
    if (cadence === 'thorough') return [1, 2, 3, 4, 5]
    return [1, 2, 4, 5]
  }, [cadence])

  // Figure out each row's state glyph.
  const briefingDone = briefingStatus === 'approved'
  // Plan §P1.10 — the audit flagged that we lie when the briefing is
  // approved but Phase 1 hasn't run yet. Both states focus the same
  // surface (the briefing panel hosts the Run button), but the user
  // sees the rail say "Phase 1 briefing" while the panel itself shows
  // an approved briefing → confusion. We keep the focus key as
  // ``briefing`` (the surface IS the briefing panel) but track a
  // sub-state so the rail label and "Now" sub copy can read "Run
  // Phase 1" instead.
  const inPhase1Run = briefingDone && phaseStates[1] !== 'done'

  // Audit §3.1.2 / plan §P2.14 — collapse the Done section by default
  // when there are 3+ rows so the rail focuses attention on the active
  // and upcoming work. The user can expand inline.
  const doneRows: Array<{
    key: string
    label: string
    sub: string
    onClick: () => void
  }> = []
  if (briefingDone) {
    doneRows.push({
      key: 'briefing-done',
      label: 'Phase 1 briefing',
      sub: 'approved',
      onClick: () => setFocus('briefing'),
    })
  }
  if (phaseStates[1] === 'done') {
    doneRows.push({
      key: 'phase-1-done',
      label: 'P1 — Contextualist',
      sub: '~45–60 s',
      onClick: () => setFocus('pipeline2'),
    })
  }
  doneRows.push({
    key: 'intake-done',
    label: 'Intake',
    sub: '3 steps · project created',
    onClick: () => undefined,
  })
  const collapseDoneByDefault = doneRows.length >= 3
  const [doneOpen, setDoneOpen] = useState(!collapseDoneByDefault)
  // If the threshold flips (more rows appear), auto-collapse on next render.
  useEffect(() => {
    setDoneOpen(!collapseDoneByDefault)
  }, [collapseDoneByDefault])

  return (
    <aside className="bg-slate-950/80 border-r border-slate-800 p-3 flex flex-col overflow-y-auto">
      <div className="px-1 pb-2">
        <div className="flex items-center gap-2 mb-2">
          <GitMerge className="h-4 w-4 text-indigo-400" />
          <span className="text-xs font-semibold tracking-wide text-slate-200">Timeline</span>
          <span className="ml-auto text-[10px] text-slate-600 font-mono">run #{runId}</span>
        </div>
        <div className="text-sm font-semibold text-slate-100 leading-tight truncate">
          {projectName}
        </div>
        <div className="text-[10.5px] text-slate-600 font-mono mt-0.5 truncate">{projectId}</div>
      </div>
      <div className="h-px bg-slate-800 mb-2" />

      {/* NOW section */}
      <div className="alm-ti-section">Now</div>
      {firstUnresolved ? (
        <RailRow
          label={firstUnresolvedLabel(firstUnresolved, inPhase1Run)}
          sub={firstUnresolvedSub(firstUnresolved, inPhase1Run)}
          tone="now"
          active={focus === firstUnresolved}
          onClick={() => setFocus(firstUnresolved)}
          iconText="!"
        />
      ) : (
        <RailRow
          label="Everything done"
          sub="Head to Export to download the bundle"
          tone="done"
          active={focus === 'export'}
          onClick={() => setFocus('export')}
          iconText="✓"
        />
      )}

      {/* UP NEXT */}
      <div className="alm-ti-section mt-2">Up next</div>

      <RailRow
        label="P2 — Observer"
        sub="Constraint extraction"
        tone={phaseStates[2] === 'done' ? 'done' : phaseStates[2] === 'ready' ? 'rdy' : 'blk'}
        active={focus === 'pipeline2'}
        onClick={() => setFocus('pipeline2')}
        iconText="2"
        locked={isLocked('pipeline2')}
      />
      {visibleGates.includes(2) && (
        <RailRow
          label="G2 · Constraint review"
          sub="After Phase 2"
          tone={phaseStates[2] === 'done' ? 'rdy' : 'blk'}
          active={focus === 'gate2'}
          onClick={() => setFocus('gate2')}
          iconText="G"
          locked={isLocked('gate2')}
        />
      )}
      {hasComplianceCues && (
        <RailRow
          label="G2.5 · Compliance Triage"
          sub="Triage injected stubs"
          tone={phaseStates[2] === 'done' ? 'rdy' : 'blk'}
          active={focus === 'gate25'}
          onClick={() => setFocus('gate25')}
          iconText="G"
          locked={isLocked('gate25')}
        />
      )}
      <RailRow
        label="P3 — Generation swarm"
        sub="Agents run in parallel"
        tone={phaseStates[3] === 'done' ? 'done' : phaseStates[3] === 'ready' ? 'rdy' : 'blk'}
        active={focus === 'pipeline3'}
        onClick={() => setFocus('pipeline3')}
        iconText="3"
        locked={isLocked('pipeline3')}
      />
      {visibleGates.includes(3) && (
        <RailRow
          label="G3 · Swarm sanity"
          sub="Spot-check BRD / SRD / compliance"
          tone={phaseStates[3] === 'done' ? 'rdy' : 'blk'}
          active={focus === 'gate3'}
          onClick={() => setFocus('gate3')}
          iconText="G"
          locked={isLocked('gate3')}
        />
      )}
      {/* Q7 G3.5 Coherence Gate — visible at standard + thorough
          (matches the backend ``gate_emitter._GATE_VISIBILITY``).
          Becomes ready as soon as Phase 3 finishes; the backend
          surfaces a confirm-and-advance row when Q5 dedup + Q7
          citation verifier ran clean. */}
      {cadence !== 'minimal' && (
        <RailRow
          label="G3.5 · Coherence"
          sub="Citation + dedup review"
          tone={phaseStates[3] === 'done' ? 'rdy' : 'blk'}
          active={focus === 'gate35'}
          onClick={() => setFocus('gate35')}
          iconText="G"
          locked={isLocked('gate35')}
        />
      )}
      {/* Gate G3.7 (plan/AI_ALM_Gate_G3_7_Implementation_Plan.md §8) —
          SRD Technical Depth Audit. Visibility matches G3.5 (standard
          + thorough budgets only). Becomes ready after Phase 3 lands
          ``depth-findings.json`` on disk; the panel surfaces a
          confirm-and-advance row when the probe walked the rubric
          clean. */}
      {cadence !== 'minimal' && (
        <RailRow
          label="G3.7 · Depth audit"
          sub="Per-shape rubric findings"
          tone={phaseStates[3] === 'done' ? 'rdy' : 'blk'}
          active={focus === 'gate37'}
          onClick={() => setFocus('gate37')}
          iconText="G"
          locked={isLocked('gate37')}
        />
      )}
      <RailRow
        label="P4 — Critic audit"

        sub="Deterministic · no LLM"
        tone={phaseStates[4] === 'done' ? 'done' : phaseStates[4] === 'ready' ? 'rdy' : 'blk'}
        active={focus === 'pipeline4'}
        onClick={() => setFocus('pipeline4')}
        iconText="4"
        locked={isLocked('pipeline4')}
      />
      {visibleGates.includes(4) && (
        <RailRow
          label="G4 · Rewrite review"
          sub="Accept / reject rewrites"
          tone={phaseStates[4] === 'done' ? 'rdy' : 'blk'}
          active={focus === 'gate4'}
          onClick={() => setFocus('gate4')}
          iconText="G"
          locked={isLocked('gate4')}
        />
      )}
      <RailRow
        label="P5 — Export"
        sub="Bundle & sign"
        tone={phaseStates[5] === 'done' ? 'done' : phaseStates[5] === 'ready' ? 'rdy' : 'blk'}
        active={focus === 'pipeline5' || focus === 'export'}
        onClick={() => setFocus('pipeline5')}
        iconText="5"
        locked={isLocked('pipeline5')}
      />
      {visibleGates.includes(5) && (
        <RailRow
          label="G5 · Final review"
          sub="Sign off on the bundle"
          tone={phaseStates[5] === 'done' ? 'rdy' : 'blk'}
          active={focus === 'gate5'}
          onClick={() => setFocus('gate5')}
          iconText="G"
          locked={isLocked('gate5')}
        />
      )}

      {/* INPUTS — promoted into the rail (plan §P2.15 / audit §3.1.5).
          Was previously buried inside the Inspector's right-hand
          ``Inputs`` tab; users coming back to the project couldn't
          find where to upload more files. Now it's a first-class row. */}
      <div className="alm-ti-section mt-2">Approval Chain</div>
      <RailRow
        label="Required signatories"
        sub="Fill named approver slots driven by regulatory flags"
        tone="rdy"
        active={focus === 'approvalChain'}
        onClick={() => setFocus('approvalChain')}
        iconText="A"
      />
      {/* 2026-05-28 — AI-governance ratification row. Always visible
          (not gated on AI signal detection) because the SR text that
          triggers the gate is generated in Phase 3 and the architect
          may want to ratify proactively. The card itself surfaces an
          already-ratified state when the intake already carries one. */}
      <RailRow
        label="AI Governance"
        sub="Ratify AI-derived outputs (closes PEG-AI-GOVERNANCE-SIGNATURE-001)"
        tone="rdy"
        active={focus === 'aiGovernance'}
        onClick={() => setFocus('aiGovernance')}
        iconText="AI"
      />

      <div className="alm-ti-section mt-2">Inputs</div>
      <RailRow
        label="Uploads & format docs"
        sub="Add or remove materials Phase 2 / 5 will read"
        tone="rdy"
        active={focus === 'inputs'}
        onClick={() => setFocus('inputs')}
        iconText="↑"
        rowClassName="alm-ti-inputs"
      />

      {/* DONE — collapsed by default when 3+ rows (plan §P2.14). */}
      <div className="mt-2">
        {collapseDoneByDefault ? (
          <button
            type="button"
            className="alm-section-toggle"
            onClick={() => setDoneOpen((v) => !v)}
            aria-expanded={doneOpen}
            data-open={doneOpen}
          >
            <ChevronRight className="h-3 w-3 alm-chev" />
            <span>Done · {doneRows.length}</span>
          </button>
        ) : (
          <div className="alm-ti-section">Done</div>
        )}
        {doneOpen
          ? doneRows.map((row) => (
              <RailRow
                key={row.key}
                label={row.label}
                sub={row.sub}
                tone="done"
                active={false}
                onClick={row.onClick}
                iconText="✓"
              />
            ))
          : null}
      </div>

      <div className="mt-auto pt-3 px-1 text-[10.5px] text-slate-600 leading-relaxed border-t border-slate-800 mt-2">
        Click any row to focus it in the center pane. Press{' '}
        <kbd className="alm-kbd">[</kbd> / <kbd className="alm-kbd">]</kbd> to cycle, or{' '}
        <kbd className="alm-kbd">?</kbd> for the full shortcut list.
      </div>
    </aside>
  )
}

function RailRow({
  label,
  sub,
  tone,
  active,
  onClick,
  iconText,
  rowClassName,
  locked = false,
}: {
  label: string
  sub?: string
  tone: 'now' | 'run' | 'done' | 'blk' | 'rdy'
  active: boolean
  onClick: () => void
  iconText: string
  rowClassName?: string
  /**
   * When true the row's prerequisites (an earlier phase or gate) are
   * not yet satisfied. The row renders as a disabled, non-interactive
   * button so the architect cannot jump ahead in the pipeline — the
   * strict ordering guard. See the ``ladder`` derivation in
   * ``TimelineRail``.
   */
  locked?: boolean
}) {
  return (
    <button
      type="button"
      disabled={locked}
      aria-disabled={locked || undefined}
      title={locked ? 'Complete the previous step first' : undefined}
      className={`alm-ti-row w-full text-left ${
        locked ? 'opacity-40 cursor-not-allowed' : ''
      } ${rowClassName ?? ''}`}
      data-active={active}
      data-locked={locked || undefined}
      onClick={locked ? undefined : onClick}
    >
      <span className={`alm-ti-dot alm-ti-${tone}`}>{locked ? '🔒' : iconText}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-200 leading-tight">{label}</div>
        {sub && <div className="text-[11px] text-slate-500 leading-tight">{sub}</div>}
      </div>
    </button>
  )
}

function firstUnresolvedLabel(f: FocusKey, inPhase1Run = false): string {
  switch (f) {
    case 'briefing':
      return inPhase1Run ? 'Run Phase 1' : 'Phase 1 briefing'
    case 'gate1': return 'Gate G1 — Phase 1 review'
    case 'pipeline2': return 'Run Phase 2'
    case 'pipeline3': return 'Run Phase 3'
    case 'pipeline4': return 'Run Phase 4'
    case 'pipeline5': return 'Run Phase 5 · Export'
    case 'gate2': return 'G2 · Constraint review'
    case 'gate25': return 'G2.5 · Compliance Triage'
    case 'gate3': return 'G3 · Swarm sanity'
    case 'gate35': return 'G3.5 · Coherence'
    case 'gate37': return 'G3.7 · Depth audit'
    case 'gate4': return 'G4 · Rewrite review'
    case 'gate5': return 'G5 · Final review'
    default: return 'Next step'
  }
}
function firstUnresolvedSub(f: FocusKey, inPhase1Run = false): string {
  switch (f) {
    case 'briefing':
      return inPhase1Run
        ? 'Briefing approved · run Phase 1 when ready'
        : 'Approve before spending any tokens'
    case 'gate1': return 'Review restated problem + nouns'
    case 'pipeline2': return 'Observer extracts constraints'
    case 'pipeline3': return '5–10 min · 5–7 agents'
    case 'pipeline4': return 'Deterministic · no LLM spend'
    case 'pipeline5': return 'Assemble final deliverable'
    case 'gate2':
    case 'gate25':
    case 'gate3':
    case 'gate35':
    case 'gate37':
    case 'gate4':
    case 'gate5':
      return 'Answer the pending decisions before continuing'
    default: return ''
  }
}

// ---------------------------------------------------------------------- //
// FocusSurface (CENTER)
// ---------------------------------------------------------------------- //
function FocusSurface({
  projectId,
  runId,
  project,
  focus,
  setFocus,
  phaseStates,
  cadence,
  hasComplianceCues,
  runnablePhase,
  legacyBriefing = false,
}: {
  projectId: string
  runId: number
  project: NonNullable<ReturnType<typeof useQuery<import('@/types').Project>>['data']>
  focus: FocusKey
  setFocus: (f: FocusKey) => void
  phaseStates: Record<1 | 2 | 3 | 4 | 5, 'done' | 'blocked' | 'ready'>
  cadence: 'minimal' | 'standard' | 'thorough'
  hasComplianceCues: boolean
  // The one phase (2–5) whose turn it is per the linear flow, or null
  // when a gate must be answered first. Drives the Pipeline Runner's
  // Run buttons — no gate logic lives in the runner.
  runnablePhase: 2 | 3 | 4 | 5 | null
  legacyBriefing?: boolean
}) {

  const meta = FOCUS_META[focus]

  if (focus === 'briefing' && !legacyBriefing) {
    return (
      <CarbonPhaseBriefingPanel
        projectId={projectId}
        runId={runId}
        onApproved={() => setFocus('briefing')}
        onPhaseComplete={() => setFocus('gate1')}
      />
    )
  }

  // ------------------------------------------------------------------ //
  // Auto-advance targets. Gate panels call these after the architect
  // resolves (approves / skips) so the surface jumps to the *next
  // step that just became actionable* — never past a still-required
  // gate. Both must respect the same visibility rules the rail uses:
  //   * G2 (constraint review) is visible at standard + thorough
  //     (``cadence !== 'minimal'``).
  //   * G2.5 (compliance triage) is visible whenever the intake
  //     declared regulatory cues (``hasComplianceCues``), independent
  //     of cadence.
  // ``afterPhase2`` is where G1's post-Phase-2 approval lands (Phase 1
  // gate runs Phase 2, then advances); ``afterG2`` is where a resolved
  // G2 lands. Getting these wrong is how the pipeline used to skip the
  // constraint-review gates entirely — see the ordering-guard audit.
  // ------------------------------------------------------------------ //
  const afterPhase2: FocusKey =
    cadence !== 'minimal' ? 'gate2' : hasComplianceCues ? 'gate25' : 'pipeline3'
  const afterG2: FocusKey = hasComplianceCues ? 'gate25' : 'pipeline3'

  // Plan §P2.13 — render the breadcrumb above every focus surface so
  // the user always knows where they are in the 5-phase ladder. The
  // legacy panels render their own headers, so we layer the
  // breadcrumb above them as a non-intrusive eyebrow row. Surfaces
  // that already use ``PageHeader`` (PipelineRunner, Briefing, gates
  // built post-P1.8) get the breadcrumb wrapped above their header
  // for now; the eyebrow is the same shape as the rest of the
  // ``alm-breadcrumb`` rows so nothing competes with it.
  return (
    <section className="bg-slate-950/40 overflow-y-auto">
      <div className="max-w-3xl mx-auto px-8 py-8">
        {meta ? (
          <nav aria-label="Breadcrumb" className="alm-breadcrumb mb-3">
            <span>Command Center</span>
            <span aria-hidden className="alm-breadcrumb-sep">›</span>
            <span>{meta.stage}</span>
            <span aria-hidden className="alm-breadcrumb-sep">›</span>
            <span>{meta.position}</span>
          </nav>
        ) : null}

        {focus === 'briefing' && (
          <div className="alm-fadein">
            <PhaseBriefingPanel
              projectId={projectId}
              runId={runId}
              phase={1}
              // P1/G1 UX fix plan §A1 — approving the briefing used to
              // jump straight to Gate G1, but G1 is empty until Phase 1
              // actually runs ("Waiting for Phase 1 to complete…"). Stay
              // on the briefing where the Run Phase 1 button appears.
              onApproved={() => setFocus('briefing')}
              // §A2 — when Phase 1 completes, auto-advance to G1 so the
              // user never has to hunt for the review surface.
              onPhaseComplete={() => setFocus('gate1')}
            />
          </div>
        )}

        {focus === 'gate1' && (
          <ContentAwareG1Gate
            project={project}
            runId={runId}
            onApproved={() => setFocus(afterPhase2)}
            // §A3 — if the user lands on G1 before Phase 1 has produced
            // the charter, offer a one-click way back to the briefing
            // (where the approve/run actions live).
            onNavigateToBriefing={() => setFocus('briefing')}
          />
        )}

        {(focus === 'pipeline2' ||
          focus === 'pipeline3' ||
          focus === 'pipeline4' ||
          focus === 'pipeline5') && (
          <div className="alm-fadein">
            <PipelineRunner
              projectId={projectId}
              runId={runId}
              runnablePhase={runnablePhase}
            />
          </div>
        )}

        {focus === 'gate2' && (
          <div className="alm-fadein">
            <SkipGate
              focusKey="gate2"
              onSkip={(reason) => skipGate(projectId, runId, 2, reason).then(() => setFocus(afterG2))}
            />
            <ContentAwareG2Gate
              project={project}
              runId={runId}
              onApproved={() => setFocus(afterG2)}
              nextIsGate25={hasComplianceCues}
            />
          </div>
        )}

        {focus === 'gate25' && (
          <>
            <SkipGate
              focusKey="gate25"
              onSkip={(reason) => skipGate(projectId, runId, 25, reason).then(() => setFocus('pipeline3'))}
            />
            <ComplianceTriageSurface
              projectId={projectId}
              runId={runId}
              onApproved={() => setFocus('pipeline3')}
            />
          </>
        )}
        {focus === 'gate3' && (
          <div className="alm-fadein">
            <SkipGate
              focusKey="gate3"
              onSkip={(reason) => skipGate(projectId, runId, 3, reason).then(() => setFocus('gate35'))}
            />
            <ContentAwareG3Gate project={project} runId={runId} onApproved={() => setFocus('gate35')} />
          </div>
        )}
        {focus === 'gate35' && (
          <div className="alm-fadein">
            <SkipGate
              focusKey="gate35"
              onSkip={(reason) => skipGate(projectId, runId, 35, reason).then(() => setFocus('gate37'))}
            />
            <ContentAwareG35Gate
              project={project}
              runId={runId}
              onApproved={() => setFocus('gate37')}
            />
          </div>
        )}
        {/* Gate G3.7 Phase 6 (plan §8) — SRD Technical Depth Audit.
            The architect approves / edits / defers / rejects each
            depth-probe finding before Phase 4 runs. Skip-with-reason
            is supported (gate is optional per gateOptionality.ts);
            both Skip and Apply advance into the Critic. */}
        {focus === 'gate37' && (
          <div className="alm-fadein">
            <SkipGate
              focusKey="gate37"
              onSkip={(reason) => skipGate(projectId, runId, 37, reason).then(() => setFocus('pipeline4'))}
            />
            <ContentAwareG37Gate
              project={project}
              runId={runId}
              onApproved={() => setFocus('pipeline4')}
            />
          </div>
        )}
        {focus === 'gate4' && (

          <div className="alm-fadein">
            <ContentAwareG4Gate project={project} runId={runId} onApproved={() => setFocus('pipeline5')} />
          </div>
        )}
        {focus === 'gate5' && (
          <div className="alm-fadein">
            <ContentAwareG5Gate project={project} runId={runId} onApproved={() => setFocus('export')} />
          </div>
        )}

        {focus === 'artifacts' && (
          <div className="alm-fadein">
            <ArtifactViewer projectId={projectId} runId={runId} />
          </div>
        )}

        {focus === 'inputs' && (
          <div className="alm-fadein">
            <InputsFocus projectId={projectId} />
          </div>
        )}

        {focus === 'approvalChain' && (
          <div className="alm-fadein">
            {/* ApprovalChainEditor owns its own success toast — do
                NOT fire another one from `onSaved` or the user sees
                two identical "Approval chain saved" messages. The
                prop is kept available for future side effects (e.g.
                focusing the next surface). */}
            <ApprovalChainEditor projectId={projectId} />
          </div>
        )}

        {focus === 'aiGovernance' && (
          <div className="alm-fadein">
            {/* Post-intake AI-governance ratification — covers the
                case where the wizard's Step-3 block didn't surface
                at intake but the generated BR/SR text declares an
                AI-derived output, so PEG-AI-GOVERNANCE-SIGNATURE-001
                blocks Phase 5. The card owns its own success toast. */}
            <AiGovernanceRatificationCard projectId={projectId} />
          </div>
        )}

        {focus === 'export' && (
          <div className="alm-fadein">
            <DownloadCenter projectId={projectId} runId={runId} />
          </div>
        )}

        {/* Safety fallback — if focus is unset, offer the nearest CTA. */}
        {!focus && (
          <div className="text-slate-400 text-sm">
            {phaseStates[1] === 'done' ? 'Pick a step on the left.' : 'Review the Phase 1 briefing on the left.'}
          </div>
        )}
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------- //
// InputsFocus — center-pane host for the Inputs surface, promoted
// from the right-hand Inspector tab (plan §P2.15). Re-uses the same
// InputUploader the Inspector renders so behaviour is identical; the
// surface just lives in a more discoverable place.
// ---------------------------------------------------------------------- //
function InputsFocus({ projectId }: { projectId: string }) {
  const inputs = useQuery({
    queryKey: ['inputs', projectId],
    queryFn: () => api.listInputs(projectId),
    enabled: Boolean(projectId),
    retry: false,
  })
  const contentInputs = (inputs.data?.inputs ?? []).filter((r) => r.kind === 'content')
  const formatInputs = (inputs.data?.inputs ?? []).filter((r) => r.kind === 'format')

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="alm-chip alm-chip-info">
              <Upload className="h-3 w-3" /> Inputs
            </span>
            <span className="alm-chip">
              {(inputs.data?.inputs ?? []).length} file
              {(inputs.data?.inputs ?? []).length === 1 ? '' : 's'}
            </span>
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-100">
            Project inputs
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Add specs, vendor PDFs, formatting docs, or pasted text the
            agents should consider. <strong>Content</strong> docs feed
            Phase 2 (Observer); <strong>format</strong> docs inform Phase 5
            export styling.
          </p>
        </div>
      </div>

      {inputs.isLoading ? (
        <div className="space-y-2">
          <SkeletonRow />
          <SkeletonRow />
        </div>
      ) : (
        <div className="space-y-3">
          <InputUploader
            mode="live"
            kind="content"
            title="Content documents"
            description="Fed to Phase 2 (Observer) for constraint extraction."
            hint="Drop specs, schemas, vendor PDFs here"
            projectId={projectId}
            rows={contentInputs}
            onChanged={() => inputs.refetch()}
          />
          <InputUploader
            mode="live"
            kind="format"
            title="Formatting instructions"
            description="Style hints for the Phase 5 export."
            hint="Drop sample requirement docs / brand guides here"
            projectId={projectId}
            rows={formatInputs}
            onChanged={() => inputs.refetch()}
          />
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------- //
// InspectorRail (RIGHT)
// ---------------------------------------------------------------------- //
type InspectorTab =
  | 'intent'
  | 'inputs'
  | 'artifacts'
  | 'structure'
  | 'budget'
  | 'log'

function InspectorRail({
  projectId,
  runId,
  context,
  index,
  indexLoading,
}: {
  projectId: string
  runId: number
  context: ProjectContext | null
  index: ArtifactIndex | null
  indexLoading: boolean
}) {
  const [tab, setTab] = useState<InspectorTab>('intent')
  const uxProject = useUxStore((s) => s.projects[projectId])
  const shape = uxProject?.shape ? getShape(uxProject.shape) : null
  const tokensUsed = usePipelineStore((s) => s.tokensUsed)
  const tokensBudget = usePipelineStore((s) => s.tokensBudget)
  const rqs = usePipelineStore((s) => s.rqs)
  const pct = tokensBudget > 0 ? Math.min(100, (tokensUsed / tokensBudget) * 100) : 0

  // Coverage Map (Decision Surface plan, Stage 1a). The "Document" tab
  // appears only when the backend route is mounted — i.e. the
  // `coverage_map_enabled` flag is on. A 404 (flag off) leaves the tab
  // hidden, so "off" stays visually unchanged. Shares the panel's
  // queryKey, so react-query dedupes to a single fetch.
  // TODO(coverage-map 1b): fold the flag into the public settings probe
  // (cf. g4_settings) to drop this speculative request when off.
  const structure = useQuery({
    queryKey: ['document-structure', projectId, runId],
    queryFn: () => getDocumentStructure(projectId, runId),
    enabled: Boolean(projectId),
    retry: false,
  })
  const coverageMapAvailable = Boolean(structure.data)

  // User-supplied inputs (uploaded content + formatting docs + free-form
  // text). Refetched on demand so adding/removing an input from the
  // dropzone updates the count immediately.
  const inputs = useQuery({
    queryKey: ['inputs', projectId],
    queryFn: () => api.listInputs(projectId),
    enabled: Boolean(projectId),
    retry: false,
  })
  const contentInputs = (inputs.data?.inputs ?? []).filter((r) => r.kind === 'content')
  const formatInputs = (inputs.data?.inputs ?? []).filter((r) => r.kind === 'format')
  const textInputs = (inputs.data?.inputs ?? []).filter((r) => r.kind === 'text')

  // Load charter for "Intent" summary (same as G1). Gate the fetches on
  // the artifact-index actually listing the file — otherwise a
  // freshly-created project (Phase 1 not yet run) hits the file server
  // every render and floods the console with 404s.
  const charterPresent = (index?.files ?? []).some(
    (f) => f.name === 'project-charter.md',
  )
  const intentMapPresent = (index?.files ?? []).some(
    (f) => f.name === 'intent-map.md',
  )
  const [charter, setCharter] = useState('')
  const [intentMap, setIntentMap] = useState('')
  useEffect(() => {
    if (!charterPresent && !intentMapPresent) return
    let cancelled = false
    async function load() {
      if (charterPresent) {
        try {
          const r1 = await fetch(
            projectFileUrl(projectId, `${runFolder(runId)}/phase1/project-charter.md`),
          )
          if (r1.ok && !cancelled) setCharter(await r1.text())
        } catch { /* ignore */ }
      }
      if (intentMapPresent) {
        try {
          const r2 = await fetch(
            projectFileUrl(projectId, `${runFolder(runId)}/phase1/intent-map.md`),
          )
          if (r2.ok && !cancelled) setIntentMap(await r2.text())
        } catch { /* ignore */ }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [projectId, runId, charterPresent, intentMapPresent])

  const summary = firstParagraph(stripFrontmatter(charter)).slice(0, 360)
  const goals = extractGoals(intentMap).slice(0, 8)

  // Phase filter for artifact tab.
  const phases: Record<number, ArtifactIndexFile[]> = {}
  for (const f of index?.files ?? []) {
    if (!phases[f.phase]) phases[f.phase] = []
    phases[f.phase]!.push(f)
  }

  const intentLoading = !charter && !intentMap && (charterPresent || intentMapPresent)

  return (
    <aside className="bg-slate-950/80 border-l border-slate-800 flex flex-col overflow-hidden">
      <div className="flex items-center border-b border-slate-800">
        <TabButton active={tab === 'intent'} onClick={() => setTab('intent')} icon={Target}>
          Intent
        </TabButton>
        <TabButton active={tab === 'inputs'} onClick={() => setTab('inputs')} icon={Upload}>
          Inputs
        </TabButton>
        <TabButton active={tab === 'artifacts'} onClick={() => setTab('artifacts')} icon={FolderGit}>
          Files
        </TabButton>
        {coverageMapAvailable && (
          <TabButton
            active={tab === 'structure'}
            onClick={() => setTab('structure')}
            icon={MapIcon}
          >
            Document
          </TabButton>
        )}
        <TabButton active={tab === 'budget'} onClick={() => setTab('budget')} icon={Gauge}>
          Budget
        </TabButton>
        <TabButton active={tab === 'log'} onClick={() => setTab('log')} icon={History}>
          Log
        </TabButton>
      </div>

      <div className="p-4 overflow-y-auto flex-1 space-y-3">
        {tab === 'structure' && coverageMapAvailable && (
          <CoverageMapPanel projectId={projectId} runId={runId} />
        )}
        {tab === 'intent' && (
          <>
            {intentLoading ? (
              <SkeletonCard lines={4} />
            ) : summary ? (
              <div className="alm-card">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1.5">
                  Project charter · excerpt
                </div>
                <div className="text-[13px] leading-relaxed text-slate-200">{summary}</div>
              </div>
            ) : (
              <div className="alm-card">
                <div className="text-xs text-slate-500">
                  Waiting for Phase 1 to produce the charter.
                </div>
              </div>
            )}

            {goals.length > 0 && (
              <div className="alm-card">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-2">
                  Goal hierarchy
                </div>
                <ul className="text-[13px] leading-snug space-y-1 text-slate-200">
                  {goals.map((g, i) => (
                    <li
                      key={`${g.text}-${i}`}
                      className="flex gap-1.5"
                      style={{ paddingLeft: `${g.depth * 12}px` }}
                    >
                      <span className={g.depth === 0 ? 'text-indigo-400' : 'text-slate-500'}>
                        {g.depth === 0 ? '●' : '◦'}
                      </span>
                      {g.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {context && (
              <div className="alm-card">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-2">
                  Intake summary
                </div>
                <div className="space-y-1.5 text-[12px]">
                  {shape && (
                    <Row label="Shape" value={shape.label} />
                  )}
                  {uxProject?.vertical && (
                    <Row label="Vertical" value={uxProject.vertical} />
                  )}
                  {uxProject?.audience && uxProject.audience.length > 0 && (
                    <Row
                      label="Audience"
                      value={uxProject.audience
                        .map((a) => AUDIENCE_OPTIONS.find((x) => x.id === a)?.label ?? a)
                        .join(' · ')}
                    />
                  )}
                  {/* Cadence row mirrors the backend's effective budget
                      (forced ?? raw) — see ``gate_enforcer.py``. When
                      an escalation occurred we surface both values so
                      the architect understands why extra gates appear. */}
                  {context.derived.forced_intervention_budget &&
                  context.derived.forced_intervention_budget !==
                    context.intake.intervention_budget ? (
                    <Row
                      label="Cadence"
                      value={`${context.derived.forced_intervention_budget} (forced from ${context.intake.intervention_budget})`}
                      cap
                    />
                  ) : (
                    <Row label="Cadence" value={context.intake.intervention_budget} cap />
                  )}
                  <Row label="Profile" value={context.derived.profile} />
                  <Row label="ASVS" value={`L${context.derived.asvs_level}`} />
                  <Row label="Tone" value={context.derived.tone_register} />
                  <Row
                    label="Template"
                    value={context.intake.output_template.mode.replace('_', ' ')}
                  />
                  {/* Recipe surface — shows the user the exact swarm
                      shape that Phase 3 will run, so the choice they
                      made on the Smart Intake "Shape" step is no
                      longer cosmetic. See brain/15-workflow-recipes.md
                      and tests/unit/test_recipe_registry.py for the
                      enforcement contract. */}
                  {context.derived.recipe_label && (
                    <Row label="Recipe" value={context.derived.recipe_label} />
                  )}
                </div>
                {context.derived.recipe_agents &&
                  context.derived.recipe_agents.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-700/50">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
                        Phase 3 agents · {context.derived.recipe_agents.length} active
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {context.derived.recipe_agents.map((a) => (
                          <span
                            key={a}
                            className="alm-chip alm-chip-info text-[10px]"
                            title="Will run during Phase 3"
                          >
                            {AGENT_LABELS[a] ?? a}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            )}
          </>
        )}

        {tab === 'inputs' && (
          <div className="space-y-3">
            <div className="alm-card">
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1">
                What you've supplied
              </div>
              <div className="text-[12px] text-slate-300 leading-relaxed">
                Everything you upload here is saved under{' '}
                <code className="font-mono text-indigo-300">inputs/</code> in the
                project directory and shipped inside the audit-package zip.
                Phase 2 (Observer) reads <strong>content</strong> docs;
                Phase 5 (Exporter) consults <strong>format</strong> docs for
                styling cues.
              </div>
            </div>

            {/* Problem statement / pasted text */}
            {textInputs.length > 0 && (
              <div className="alm-card">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1.5">
                  Typed inputs
                </div>
                <ul className="space-y-1.5">
                  {textInputs.map((row) => (
                    <li
                      key={row.input_id}
                      className="rounded border border-slate-700/50 bg-slate-900/60 p-2"
                    >
                      <div className="text-[12px] font-semibold text-slate-100">
                        {row.label}
                      </div>
                      {row.excerpt && (
                        <div className="text-[11px] text-slate-400 mt-1 line-clamp-3 whitespace-pre-wrap">
                          {row.excerpt}
                        </div>
                      )}
                      <a
                        href={projectFileUrl(projectId, row.stored_path)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[10.5px] text-indigo-300 hover:underline mt-1 inline-block"
                      >
                        open file →
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <InputUploader
              mode="live"
              kind="content"
              title="Content documents"
              description="Fed to Phase 2 (Observer) for constraint extraction."
              hint="Drop specs, schemas, vendor PDFs here"
              projectId={projectId}
              rows={contentInputs}
              onChanged={() => inputs.refetch()}
            />

            <InputUploader
              mode="live"
              kind="format"
              title="Formatting instructions"
              description="Style hints for the Phase 5 export."
              hint="Drop sample requirement docs / brand guides here"
              projectId={projectId}
              rows={formatInputs}
              onChanged={() => inputs.refetch()}
            />
          </div>
        )}

        {tab === 'artifacts' && (
          <>
            {indexLoading ? (
              <div className="space-y-2">
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </div>
            ) : !index || index.files.length === 0 ? (
              <div className="text-xs text-slate-500">No artifacts produced yet.</div>
            ) : (
              Object.keys(phases)
                .sort()
                .map((p) => {
                  const pn = Number(p)
                  const files = phases[pn] ?? []
                  return (
                    <div key={p}>
                      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1">
                        Phase {pn} · {files.length} file{files.length === 1 ? '' : 's'}
                      </div>
                      <div className="space-y-1.5">
                        {files.map((f) => (
                          <a
                            key={f.path}
                            className="alm-card flex items-center gap-2 hover:bg-slate-900 transition-colors"
                            href={projectFileUrl(projectId, f.path)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <FileIcon className="h-3.5 w-3.5 text-indigo-300 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate text-slate-100">
                                {f.name}
                              </div>
                              <div className="text-[10.5px] text-slate-500 font-mono">
                                {humanBytes(f.size_bytes)}
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )
                })
            )}
          </>
        )}

        {tab === 'budget' && (
          <>
            <div className="alm-card">
              <div className="flex items-baseline justify-between">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500">
                  Tokens used
                </div>
                <div className="text-[11px] text-slate-500 font-mono tabular-nums">
                  {tokensUsed.toLocaleString()} / {tokensBudget.toLocaleString()}
                </div>
              </div>
              {/* Audit §3.3.4 / plan §P2.25 — show "Not started" until
                  the first non-zero spend lands; the meter on a brand
                  new project was misleadingly drawn at 0%. */}
              {tokensBudget > 0 && tokensUsed > 0 ? (
                <>
                  <div className="text-2xl font-bold mt-1 text-slate-100">
                    {Math.round(pct)}%
                  </div>
                  <div className="alm-progress-track mt-2">
                    <div className="alm-progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                </>
              ) : (
                <div className="text-2xl font-bold mt-1 text-slate-500">Not started</div>
              )}
            </div>
            {rqs && (
              <div className="alm-card">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1">
                  Requirements Quality
                </div>
                <div className="text-2xl font-bold text-emerald-300">
                  {rqs.overall.toFixed(1)}
                </div>
                <div className="text-[11px] text-slate-500">
                  EARS {rqs.breakdown.ears_conformance.toFixed(1)} · INCOSE{' '}
                  {rqs.breakdown.incose_40.toFixed(1)} · Trace{' '}
                  {rqs.breakdown.traceability.toFixed(1)} · Prov{' '}
                  {rqs.breakdown.provenance.toFixed(1)}
                </div>
              </div>
            )}
            <div className="alm-card">
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1">
                Rewinds
              </div>
              <div className="text-2xl font-bold text-slate-100">0</div>
              <div className="text-[11px] text-slate-500">
                Each rewind costs an extra ~$0.30 (model-dependent — see the
                phase briefing for the active provider).
              </div>
            </div>
          </>
        )}

        {tab === 'log' && (
          <DecisionLog projectId={projectId} />
        )}
      </div>
    </aside>
  )
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean
  onClick: () => void
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="alm-tab"
      data-active={active}
    >
      <Icon className="h-3.5 w-3.5" /> {children}
    </button>
  )
}

function Row({ label, value, cap }: { label: string; value: string; cap?: boolean }) {
  return (
    <div className="flex justify-between items-baseline gap-2">
      <span className="text-slate-500">{label}</span>
      <span className={`font-semibold text-slate-200 text-right ${cap ? 'capitalize' : ''}`}>
        {value}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------- //
// DecisionLog — tails history.log for quick provenance view.
// ---------------------------------------------------------------------- //
function DecisionLog({ projectId }: { projectId: string }) {
  const [lines, setLines] = useState<string[]>([])
  // Audit §3.3.3 / plan §P2.25 — surface a "live · updated N s ago"
  // indicator so the user knows the panel is polling instead of stuck.
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  // Track an ever-changing tick so the relative-age string refreshes
  // even when no new lines arrive.
  const [, setTick] = useState(0)
  useEffect(() => {
    const t = window.setInterval(() => setTick((v) => v + 1), 5000)
    return () => window.clearInterval(t)
  }, [])

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const res = await fetch(projectFileUrl(projectId, 'history.log'))
        if (!res.ok) return
        const txt = await res.text()
        if (!cancelled) {
          setLines(
            txt
              .split('\n')
              .filter((x) => x.trim())
              .slice(-30)
              .reverse(),
          )
          setLastUpdated(Date.now())
          setLoading(false)
        }
      } catch {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    const t = setInterval(load, 5000)
    return () => {
      cancelled = true
      clearInterval(t)
    }
  }, [projectId])

  if (loading) {
    return (
      <div className="space-y-2">
        <SkeletonCard lines={3} showTitle={false} />
        <SkeletonCard lines={3} showTitle={false} />
      </div>
    )
  }

  if (lines.length === 0) {
    return (
      <div className="text-xs text-slate-500">
        No entries yet. Decisions appear here as you progress through gates.
      </div>
    )
  }

  const ageSec =
    lastUpdated != null ? Math.max(1, Math.round((Date.now() - lastUpdated) / 1000)) : null
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-wider text-slate-500 mb-1">
        <span className="alm-chip alm-chip-info text-[10px]">live</span>
        <span>
          {ageSec != null
            ? ageSec < 60
              ? `updated ${ageSec} s ago`
              : `updated ${Math.round(ageSec / 60)} min ago`
            : 'updating…'}
        </span>
      </div>
      {lines.map((line, i) => (
        <div key={i} className="alm-card text-[11.5px] leading-relaxed text-slate-300 font-mono">
          {line}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------- //
// ComplianceTriageSurface — G2.5 wrapper that wires the StubTriageList
// to the surrounding "Proceed to Phase 3" CTA. Plan §1.2 acceptance
// criterion #4: the gate must NOT advance while any stub is still
// ``pending`` or ``drafted`` and unreviewed by the architect.
//
// Drafted stubs are interpreted as "drafted by the LLM, awaiting
// architect confirmation" — they still need a one-click Accept on the
// G2.5 panel before the swarm runs against them. The CTA disables
// itself with a tooltip until every stub is in ``user_confirmed`` or
// ``dropped``.
// ---------------------------------------------------------------------- //
function ComplianceTriageSurface({
  projectId,
  runId,
  onApproved,
}: {
  projectId: string
  runId: number
  onApproved: () => void
}) {
  const pack = useCompliancePackStore((s) => s.pack)
  const setPack = useCompliancePackStore((s) => s.setPack)
  const stubs = pack?.stubs ?? []
  const unresolved = stubs.filter(
    (s) => s.status === 'pending' || s.status === 'drafted',
  ).length
  const ready = pack !== null && unresolved === 0
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Persist the architect's G2.5 decisions to disk before handing off
  // to the swarm. Without this, Phase 3 reads the original drafted
  // pack and the Phase 4 compliance-completeness auditor can't see
  // which stubs the architect explicitly accepted or dropped.
  const handleProceed = async () => {
    if (!pack) return
    setSaving(true)
    setSaveError(null)
    try {
      const persisted = await api.saveCompliancePack(projectId, runId, pack)
      setPack(persisted)
      onApproved()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save compliance decisions')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="alm-fadein h-full min-h-[600px] border border-slate-700/50 rounded-md overflow-hidden flex flex-col">
      <div className="flex-1 overflow-hidden">
        <StubTriageList />
      </div>
      <div className="p-4 bg-slate-900 border-t border-slate-700/50">
        {saveError && (
          <div className="mb-2 text-sm text-rose-300" role="alert">
            {saveError}
          </div>
        )}
        {pack === null ? (
          <button
            className="alm-btn-primary w-full opacity-50 cursor-not-allowed"
            disabled
            title="Compliance pack not loaded yet"
          >
            Loading compliance pack…
          </button>
        ) : !ready ? (
          <button
            className="alm-btn-primary w-full opacity-50 cursor-not-allowed"
            disabled
            title={`Resolve ${unresolved} pending or drafted stub${unresolved === 1 ? '' : 's'} before continuing`}
          >
            Proceed to Phase 3 — {unresolved} stub{unresolved === 1 ? '' : 's'} still need
            {unresolved === 1 ? 's' : ''} review
          </button>
        ) : (
          <button
            className={`alm-btn-primary w-full ${saving ? 'opacity-60 cursor-wait' : ''}`}
            data-testid="gate-25-submit"
            onClick={handleProceed}
            disabled={saving}
          >
            {saving ? 'Saving decisions…' : 'Proceed to Phase 3 (Swarm)'}
          </button>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------- //
// skipGate — Gate G3.7 Phase 0 (plan §2.3 + §2.2.3).
//
// Submits ``status: skipped`` for every pending decision on the
// supplied gate, attaching the architect's free-text reason to each
// row. Re-uses the existing ``api.submitGateDecisions`` mutation so
// the wire contract stays unchanged — the backend's Pydantic
// validator already accepts the new ``skipped`` literal + companion
// ``skip_reason`` field after the model extension shipped earlier
// in this phase.
//
// Caller is responsible for navigating away once the promise
// resolves; the SkipGate component closes its own dialog before
// invoking ``onSkip``.
// ---------------------------------------------------------------------- //
async function skipGate(
  projectId: string,
  runId: number,
  gateNumber: number,
  reason: string,
): Promise<void> {
  // Fetch the latest gate to learn which decisions are still pending
  // — only those need flipping. Decisions already approved / edited /
  // rejected stay as-is.
  const gate = await api.getGate(projectId, runId, gateNumber).catch(() => null)
  if (!gate) {
    // Gate hasn't been emitted yet — re-emit then skip. ``createGate``
    // is idempotent so this is safe.
    await api.createGate(projectId, runId, gateNumber)
  }
  const fresh = gate ?? (await api.getGate(projectId, runId, gateNumber))
  const flipped = fresh.decisions.map((d) =>
    d.status === 'pending'
      ? { ...d, status: 'skipped' as const, skip_reason: reason }
      : d,
  )
  await api.submitGateDecisions(projectId, runId, gateNumber, flipped)
}

// (helpers `humanBytes`, `runFolder`, `stripFrontmatter`,
// `firstParagraph`, `extractGoals` now live in `@/lib/format` and
// `@/lib/markdown` — see plan §P1.9.)
